import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { streamEvents } from '@/api/sse'
import { apiUrl } from '@/config/env'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { NotificationsContext } from '@/features/notifications/NotificationsContext'
import type { NotificationItem } from '@/features/notifications/types'
import { loadSession } from '@/utils/tokenStorage'

const STREAM_WIDGETS = ['kpis', 'risks', 'alerts', 'activity', 'ai', 'imports', 'recommendations', 'trends']
const RETRY_DELAY_MS = 8_000
const MAX_ITEMS = 30

interface RawAlert {
  id: string
  title: string
  severity: string
  created_at: string
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)
  const seenIds = useRef<Set<string>>(new Set())
  const isFirstAlertsBatch = useRef(true)

  useEffect(() => {
    if (!isAuthenticated || !user?.organization_id) {
      return
    }

    const controller = new AbortController()
    let cancelled = false

    async function connectOnce() {
      const session = loadSession()
      if (!session?.accessToken) return
      const url = `${apiUrl}/dashboard/stream?token=${encodeURIComponent(session.accessToken)}&widgets=${STREAM_WIDGETS.join(',')}`

      setIsConnected(true)
      try {
        for await (const evt of streamEvents(url, { signal: controller.signal })) {
          if (!STREAM_WIDGETS.includes(evt.event)) continue
          queryClient.invalidateQueries({ queryKey: ['dashboard', evt.event] })

          if (evt.event === 'alerts') {
            try {
              const payload = JSON.parse(evt.data) as { recent?: RawAlert[] }
              const recent = payload.recent ?? []
              const fresh: NotificationItem[] = []
              for (const alert of recent) {
                if (!seenIds.current.has(alert.id)) {
                  seenIds.current.add(alert.id)
                  fresh.push({
                    id: alert.id,
                    title: alert.title,
                    severity: alert.severity,
                    createdAt: alert.created_at,
                  })
                }
              }
              if (fresh.length > 0) {
                setItems((prev) => [...fresh, ...prev].slice(0, MAX_ITEMS))
                // La primera carga solo siembra el set de "vistos"; no son alertas nuevas.
                if (!isFirstAlertsBatch.current) {
                  setUnreadCount((prev) => prev + fresh.length)
                  fresh.forEach((alert) =>
                    enqueueSnackbar(alert.title, {
                      variant: alert.severity === 'critical' || alert.severity === 'high' ? 'error' : 'warning',
                    }),
                  )
                }
                isFirstAlertsBatch.current = false
              }
            } catch {
              // Payload no parseable; se ignora este evento puntual.
            }
          }
        }
      } catch {
        // Conexión cerrada o backend sin stream disponible; se reintenta abajo.
      } finally {
        setIsConnected(false)
      }
    }

    async function loop() {
      while (!cancelled) {
        await connectOnce()
        if (cancelled) break
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
      }
    }

    loop()

    return () => {
      cancelled = true
      controller.abort()
      setIsConnected(false)
    }
  }, [isAuthenticated, user?.organization_id, queryClient, enqueueSnackbar])

  const value = useMemo(
    () => ({
      items,
      unreadCount,
      isConnected,
      markAllRead: () => setUnreadCount(0),
    }),
    [items, unreadCount, isConnected],
  )

  return <NotificationsContext value={value}>{children}</NotificationsContext>
}
